import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Vault, KeyRound, ChevronLeft, ChevronRight, Delete, CircleAlert, ShieldAlert, DoorOpen, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";

/* HEIST MINIGAMES (CEF) — /cef/heist/:serverId?token=<session>. Opened when a player
   starts cracking a safe or hacking a keypad in-game. Secrets live server-side:
   safe = 3 dial notches with a direction sequence (L→R→L), keypad = 4-digit PIN with
   5 attempts and positional feedback. Cracks pay out to the bank; every crack AND
   lockout raises a 211 silent-alarm on the police CAD. Demo floor without a token. */

const STEEL = "#1a1d21";
const TARGETS = [
  { name: "The Gilded Spade Vault", kind: "safe", take: "$5,000 – $12,000", hot: true },
  { name: "Bornwood Bank Vault", kind: "safe", take: "$2,500 – $6,000" },
  { name: "24/7 Register — Downtown", kind: "keypad", take: "$400 – $1,600" },
  { name: "Liquor Store Safe — Route 68", kind: "safe", take: "$400 – $1,600" },
  { name: "Tattoo Parlour Register", kind: "keypad", take: "$400 – $1,600" },
];
const DIR_HINT = ["Turn LEFT to the first notch", "Now RIGHT to the second", "Back LEFT for the last one"];

export default function HeistCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  /* transparent CEF chrome — the game world stays visible behind the minigame */
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);
  const sfx = usePhoneAudio();

  const [session, setSession] = useState(null); // {id, kind, target, found, attempts_left, payout}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {status: cracked|busted, payout, alarm_ref, bank}

  // safe dial state
  const [pos, setPos] = useState(0);
  const [lastDir, setLastDir] = useState("left");
  const [found, setFound] = useState(0);
  const [wobble, setWobble] = useState(false);
  const demoCombo = useRef(null);
  const holdTimer = useRef(null);
  const dialLock = useRef(false);

  // keypad state
  const [entry, setEntry] = useState("");
  const [attempts, setAttempts] = useState(5);
  const [feedbacks, setFeedbacks] = useState([]); // [{pin, exact, misplaced}]
  const demoPin = useRef(null);
  const [alarmRef, setAlarmRef] = useState(null); // early alarm (casino vault trips at notch 2)

  // crew state (second player runs crowd control)
  const [openJobs, setOpenJobs] = useState(demo ? [{ id: "demo-crew-1", target: "24/7 Register — Downtown", kind: "keypad", by: "R. O'Connor", crew: 0 }] : []);
  const [crewMode, setCrewMode] = useState(null); // {id, target, suppression}
  const holdIntimidate = useRef(null);

  useEffect(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/heist/active`, { params: { token } })
      .then((r) => {
        if (r.data.session) {
          setSession(r.data.session);
          setFound(r.data.session.found || 0);
          if (r.data.session.attempts_left != null) setAttempts(r.data.session.attempts_left);
        }
      })
      .catch(() => {});
    const fetchOpen = () => api.get(`/nc/servers/${serverId}/heist/open`, { params: { token } })
      .then((r) => setOpenJobs(r.data.jobs || [])).catch(() => {});
    fetchOpen();
    const t = setInterval(fetchOpen, 8000);
    return () => clearInterval(t);
  }, [demo, serverId, token]);

  /* ---- crew: crowd control ---- */
  const crewJoin = async (job) => {
    sfx.keyTick();
    if (demo) {
      setCrewMode({ id: job.id, target: job.target, suppression: 100, demo: true });
      toast.success("You're on crowd control — keep the room scared while they work");
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/heist/crew/join`, { session_id: job.id }, { params: { token } });
      toast.success(r.data.message);
      setCrewMode({ id: job.id, target: r.data.session.target, suppression: 100 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // suppression drains live on screen between server polls
  useEffect(() => {
    if (!crewMode) return;
    const t = setInterval(() => setCrewMode((c) => (c ? { ...c, suppression: Math.max(0, c.suppression - 0.8) } : c)), 200);
    return () => clearInterval(t);
  }, [crewMode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // server truth poll — catches crack/bust/crowd-panic
  useEffect(() => {
    if (!crewMode || crewMode.demo) return;
    const t = setInterval(() => {
      api.get(`/nc/servers/${serverId}/heist/crew/state`, { params: { token, session_id: crewMode.id } })
        .then((r) => {
          if (r.data.status !== "active") {
            const won = r.data.status === "cracked";
            setResult({ status: won ? "cracked" : "busted", payout: won ? Math.floor(r.data.payout * 0.25) : 0, alarm_ref: "211-CREW", crew: true });
            if (won) sfx.success();
            setCrewMode(null);
          } else if (r.data.suppression != null) {
            setCrewMode((c) => (c ? { ...c, suppression: r.data.suppression } : c));
          }
        }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [crewMode?.id, crewMode?.demo, demo, serverId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const intimidate = async () => {
    sfx.keyTick();
    if (crewMode?.demo) { setCrewMode((c) => ({ ...c, suppression: Math.min(100, c.suppression + 18) })); return; }
    if (!crewMode) return;
    try {
      const r = await api.post(`/nc/servers/${serverId}/heist/crew/intimidate`, { session_id: crewMode.id }, { params: { token } });
      if (r.data.suppression != null) setCrewMode((c) => ({ ...c, suppression: r.data.suppression }));
      if (r.data.status === "busted") {
        setResult({ status: "busted", payout: 0, alarm_ref: r.data.alarm_ref, crew: true });
        setCrewMode(null);
      }
    } catch { /* job may have just ended */ }
  };

  const holdStart = () => { intimidate(); holdIntimidate.current = setInterval(intimidate, 800); };
  const holdStop = () => { if (holdIntimidate.current) { clearInterval(holdIntimidate.current); holdIntimidate.current = null; } };
  useEffect(() => () => holdStop(), []);

  const start = async (t) => {
    sfx.keyTick();
    if (demo) {
      if (t.kind === "safe") {
        const combo = []; while (combo.length < 3) { const n = Math.floor(Math.random() * 100); if (!combo.includes(n)) combo.push(n); }
        demoCombo.current = combo;
      } else {
        demoPin.current = String(Math.floor(1000 + Math.random() * 9000));
      }
      setSession({ id: `demo-${Date.now()}`, kind: t.kind, target: t.name, payout: t.kind === "safe" ? (t.hot ? 8400 : 4200) : 900 });
      setFound(0); setPos(0); setEntry(""); setAttempts(5); setFeedbacks([]); setResult(null); setAlarmRef(null);
      toast.success(`Tools out — ${t.name}`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/heist/start`, { kind: t.kind, target: t.name }, { params: { token } });
      setSession(r.data.session);
      setFound(0); setPos(0); setEntry(""); setAttempts(5); setFeedbacks([]); setResult(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const finish = (out) => {
    setResult(out);
    if (out.status === "cracked") { sfx.success(); toast.success(`Cracked it — $${out.payout.toLocaleString()} lifted. Alarm ${out.alarm_ref} is live, MOVE.`); }
    else toast.error(`Lockout — silent alarm ${out.alarm_ref} tripped. Police are coming.`);
  };

  /* ---- safe dial ---- */
  const step = useCallback((dir) => {
    if (!session || result || dialLock.current) return;
    dialLock.current = true;
    setTimeout(() => { dialLock.current = false; }, 60);
    setLastDir(dir);
    setPos((p) => {
      const next = (p + (dir === "left" ? 1 : -1) + 100) % 100;
      // proximity wobble — the "feel" of the fence approaching the notch
      const combo = demoCombo.current;
      if (session.id.startsWith("demo") && combo) {
        const notch = combo[found];
        const dist = Math.min(Math.abs(next - notch), 100 - Math.abs(next - notch));
        setWobble(dist <= 5 && dir === ["left", "right", "left"][found]);
        if (dist <= 2 && dir === ["left", "right", "left"][found]) {
          const f = found + 1;
          setFound(f); sfx.success();
          if (f === 2 && /gilded|casino/i.test(session.target) && !alarmRef) setAlarmRef("211-DEMO");
          if (f >= 3) finish({ status: "cracked", payout: session.payout, alarm_ref: alarmRef || "211-DEMO", bank: null });
        } else if (Math.random() < 0.25) sfx.keyTick();
      } else {
        sfx.keyTick();
        api.post(`/nc/servers/${serverId}/heist/dial`, { session_id: session.id, position: next, direction: dir }, { params: { token } })
          .then((r) => {
            if (r.data.alarm_ref) setAlarmRef(r.data.alarm_ref);
            if (r.data.click) {
              sfx.success();
              setFound(r.data.found);
              if (r.data.status === "cracked") finish(r.data);
            }
          }).catch(() => {});
      }
      return next;
    });
  }, [session, result, found, demo, serverId, token, sfx]);

  const hold = (dir) => { step(dir); holdTimer.current = setInterval(() => step(dir), 110); };
  const release = () => { if (holdTimer.current) { clearInterval(holdTimer.current); holdTimer.current = null; } };
  useEffect(() => () => release(), []);

  /* ---- keypad ---- */
  const press = (d) => {
    if (!session || result || entry.length >= 4) return;
    sfx.keyTick();
    setEntry((e) => (e + d).slice(0, 4));
  };

  const submitPin = async () => {
    if (entry.length < 4 || busy) return;
    sfx.click?.();
    if (demo) {
      const pin = demoPin.current;
      const exact = entry.split("").filter((c, i) => c === pin[i]).length;
      const misplaced = entry.split("").filter((c, i) => c !== pin[i] && pin.includes(c)).length;
      if (entry === pin) { finish({ status: "cracked", payout: session.payout, alarm_ref: "211-DEMO", bank: null }); return; }
      setFeedbacks((f) => [{ pin: entry, exact, misplaced }, ...f]);
      setEntry("");
      const left = attempts - 1;
      setAttempts(left);
      if (left <= 0) finish({ status: "busted", payout: 0, alarm_ref: "211-DEMO" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/heist/guess`, { session_id: session.id, pin: entry }, { params: { token } });
      if (r.data.status === "cracked" || r.data.status === "busted") { finish(r.data); }
      else {
        setFeedbacks((f) => [{ pin: entry, exact: r.data.exact, misplaced: r.data.misplaced }, ...f]);
        setAttempts(r.data.attempts_left);
      }
      setEntry("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const abandon = async () => {
    if (!demo && session) await api.post(`/nc/servers/${serverId}/heist/abandon`, { session_id: session.id }, { params: { token } }).catch(() => {});
    toast.success("You slip away clean");
    setSession(null); setResult(null); setFound(0); setEntry(""); setAttempts(5); setFeedbacks([]);
  };

  const dialAngle = pos * 3.6;

  return (
    <div data-testid="heist-page" className="fixed inset-0 overflow-hidden text-zinc-100 flex items-center justify-center" style={{ background: "radial-gradient(ellipse 64% 72% at 50% 42%, rgba(7,8,10,0.96) 0%, rgba(7,8,10,0.88) 55%, rgba(4,5,6,0.05) 100%)", fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="heist minigame" />}

      {/* target select */}
      {!session && !crewMode && (
        <div className="w-full max-w-md px-6" style={{ animation: "ncFadeUp 0.4s ease both" }}>
          <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-red-400 mb-1">After hours</p>
          <h1 className="font-black uppercase tracking-[0.1em] text-2xl mb-1">Pick the job</h1>
          <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em] mb-6">Every target is alarmed — crack it before dispatch rolls units</p>
          <div className="flex flex-col gap-2">
            {TARGETS.map((t, i) => (
              <button key={t.name} data-testid={`heist-target-${i}`} disabled={busy} onClick={() => start(t)}
                className="text-left border border-white/10 bg-black/60 hover:border-red-500/50 px-5 py-4 transition-colors flex items-center gap-4 group">
                {t.kind === "safe" ? <Vault className="w-5 h-5 text-zinc-400 group-hover:text-red-400 transition-colors" /> : <KeyRound className="w-5 h-5 text-zinc-400 group-hover:text-red-400 transition-colors" />}
                <span className="flex-1">
                  <span className="font-bold text-sm block">{t.name}</span>
                  <span className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em]">{t.kind === "safe" ? "dial safe · 3 notches" : "keypad · 5 attempts"} · take {t.take}</span>
                </span>
                {t.hot && <span className="font-mono text-[7px] uppercase tracking-[0.15em] border border-red-500/50 text-red-400 px-1.5 py-1 shrink-0">High security · alarm trips early</span>}
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4 text-zinc-600" />}
              </button>
            ))}
          </div>

          {openJobs.length > 0 && (
            <div className="mt-6">
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Jobs in progress — back them up as crowd control (+25% take)</p>
              <div className="flex flex-col gap-2">
                {openJobs.map((j) => (
                  <div key={j.id} data-testid={`crew-job-${j.id.slice(0, 10)}`} className="border border-[#a78bfa33] bg-[#a78bfa08] px-5 py-3.5 flex items-center gap-4">
                    <Users className="w-4 h-4 shrink-0" style={{ color: "#a78bfa" }} />
                    <span className="flex-1 min-w-0">
                      <span className="font-bold text-sm block truncate">{j.target}</span>
                      <span className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">{j.by} is working the {j.kind === "safe" ? "dial" : "keypad"}</span>
                    </span>
                    <button data-testid={`crew-join-${j.id.slice(0, 10)}`} onClick={() => crewJoin(j)}
                      className="px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] font-bold text-black shrink-0" style={{ backgroundColor: "#a78bfa" }}>
                      Run crowd
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CREW — crowd control */}
      {crewMode && !result && (
        <div data-testid="crew-screen" className="w-full max-w-md px-6 flex flex-col items-center gap-5" style={{ animation: "ncFadeUp 0.4s ease both" }}>
          <p className="font-mono text-[9px] tracking-[0.35em] uppercase" style={{ color: "#a78bfa" }}>Crowd control — {crewMode.target}</p>
          <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.15em] text-center leading-relaxed">Keep the room intimidated while they work.<br />If the meter empties, the crowd panics and the job blows.</p>
          <div className="w-full">
            <div className="flex justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
              <span>Room nerve</span>
              <span data-testid="suppression-value" style={{ color: crewMode.suppression > 40 ? "#34d399" : crewMode.suppression > 15 ? "#fbbf24" : "#ef4444" }}>{Math.round(crewMode.suppression)}%</span>
            </div>
            <div className="h-4 border border-white/15 bg-black/60 overflow-hidden">
              <div data-testid="suppression-bar" className="h-full transition-all duration-200"
                style={{ width: `${crewMode.suppression}%`, backgroundColor: crewMode.suppression > 40 ? "#34d399" : crewMode.suppression > 15 ? "#fbbf24" : "#ef4444", boxShadow: "0 0 14px currentColor" }} />
            </div>
          </div>
          <button data-testid="intimidate-btn" onPointerDown={holdStart} onPointerUp={holdStop} onPointerLeave={holdStop}
            className="w-full py-6 font-mono text-sm uppercase tracking-[0.35em] font-bold text-black select-none touch-none active:scale-[0.98] transition-transform"
            style={{ backgroundColor: "#a78bfa", boxShadow: "0 0 30px #a78bfa44" }}>
            Hold to intimidate
          </button>
          <button data-testid="crew-leave" onClick={() => { holdStop(); setCrewMode(null); }} className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-200 transition-colors">Slip out</button>
        </div>
      )}

      {/* SAFE */}
      {session?.kind === "safe" && !result && (
        <div data-testid="safe-game" className="flex flex-col items-center gap-5 px-6" style={{ animation: "ncFadeUp 0.4s ease both" }}>
          <p className="font-mono text-[9px] tracking-[0.35em] uppercase text-zinc-500">{session.target} · take ≈ ${session.payout.toLocaleString()}</p>
          {session.crew?.length > 0 && (
            <p data-testid="crew-chip" className="font-mono text-[8px] uppercase tracking-[0.2em] px-3 py-1 border flex items-center gap-1.5" style={{ color: "#a78bfa", borderColor: "#a78bfa55", backgroundColor: "#a78bfa10" }}>
              <Users className="w-3 h-3" /> Crew linked — {session.crew[0].name} on crowd · +25% take
            </p>
          )}
          {alarmRef && (
            <p data-testid="early-alarm" className="font-mono text-[9px] uppercase tracking-[0.25em] text-red-300 border border-red-500/50 bg-red-500/10 px-4 py-2" style={{ animation: "ncPulse 1.2s ease-in-out infinite" }}>
              Silent alarm {alarmRef} live — units are rolling, keep cracking
            </p>
          )}
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <span key={i} data-testid={`notch-led-${i}`} className="w-3.5 h-3.5 rounded-full border transition-all duration-300"
                style={{ borderColor: i < found ? "#34d399" : "#3f3f46", backgroundColor: i < found ? "#34d399" : "transparent", boxShadow: i < found ? "0 0 10px #34d39988" : "none" }} />
            ))}
          </div>
          <p data-testid="dial-hint" className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">{found < 3 ? DIR_HINT[found] : "Open"}</p>

          <div className={`relative ${wobble ? "animate-pulse" : ""}`} style={{ width: 300, height: 300 }}>
            <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(#2a2e33, #17191d, #2a2e33, #17191d, #2a2e33)`, boxShadow: "0 0 50px #000, inset 0 4px 12px #ffffff11, inset 0 -4px 12px #000" }} />
            <svg data-testid="safe-dial" viewBox="0 0 300 300" className="absolute inset-0"
              style={{ transform: `rotate(${-dialAngle}deg)`, transition: "transform 0.08s linear" }}>
              {Array.from({ length: 100 }, (_, i) => {
                const big = i % 10 === 0, mid = i % 5 === 0;
                const a = i * 3.6;
                return (
                  <g key={i} transform={`rotate(${a} 150 150)`}>
                    <line x1="150" y1="12" x2="150" y2={big ? 30 : mid ? 24 : 19} stroke={big ? "#d4d4d8" : "#52525b"} strokeWidth={big ? 2.4 : 1} />
                    {big && <text x="150" y="48" textAnchor="middle" fill="#a1a1aa" fontSize="13" fontFamily="monospace" fontWeight="700" transform={`rotate(${-a} 150 48)`}>{i}</text>}
                  </g>
                );
              })}
              <circle cx="150" cy="150" r="88" fill={STEEL} stroke="#3f3f46" strokeWidth="1.5" />
              <circle cx="150" cy="150" r="30" fill="#101215" stroke="#52525b" />
              <circle cx="150" cy="150" r="6" fill="#71717a" />
            </svg>
            {/* fixed pointer */}
            <div className="absolute left-1/2 -translate-x-1/2 top-0.5 z-10" style={{ borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderTop: "12px solid #ef4444" }} />
          </div>
          <p data-testid="dial-pos" className="font-mono text-xl font-bold tracking-[0.3em]">{String(pos).padStart(2, "0")}</p>

          <div className="flex items-center gap-4">
            <button data-testid="dial-right" onPointerDown={() => hold("right")} onPointerUp={release} onPointerLeave={release}
              className="w-16 h-16 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 active:scale-95 flex items-center justify-center transition-all select-none touch-none">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 text-center w-32">hold to turn the dial · listen for the fence</p>
            <button data-testid="dial-left" onPointerDown={() => hold("left")} onPointerUp={release} onPointerLeave={release}
              className="w-16 h-16 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 active:scale-95 flex items-center justify-center transition-all select-none touch-none">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          <button data-testid="heist-abandon" onClick={abandon} className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-200 transition-colors">Walk away</button>
        </div>
      )}

      {/* KEYPAD */}
      {session?.kind === "keypad" && !result && (
        <div data-testid="keypad-game" className="flex flex-col items-center gap-4 px-6" style={{ animation: "ncFadeUp 0.4s ease both" }}>
          <p className="font-mono text-[9px] tracking-[0.35em] uppercase text-zinc-500">{session.target} · take ≈ ${session.payout.toLocaleString()}</p>
          <div className="border border-white/15 p-5 rounded-lg" style={{ background: "linear-gradient(170deg, #22262b, #121417)", boxShadow: "0 20px 50px #000, inset 0 2px 0 #ffffff11" }}>
            <div data-testid="keypad-display" className="bg-black border border-[#34d39933] px-4 py-3 mb-4 text-center font-mono text-3xl tracking-[0.6em] text-emerald-400" style={{ textShadow: "0 0 12px #34d39966", minWidth: 220 }}>
              {entry.padEnd(4, "·")}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "E"].map((k) => (
                <button key={k} data-testid={`keypad-btn-${k}`}
                  onClick={() => (k === "C" ? setEntry("") : k === "E" ? submitPin() : press(k))}
                  className={`w-16 h-14 font-mono text-lg font-bold rounded border transition-all active:scale-90 ${k === "E" ? "border-emerald-500/50 text-emerald-400" : k === "C" ? "border-red-500/50 text-red-400" : "border-white/15 text-zinc-200 bg-white/5 hover:bg-white/10"}`}>
                  {k === "E" ? <DoorOpen className="w-5 h-5 mx-auto" /> : k === "C" ? <Delete className="w-5 h-5 mx-auto" /> : k}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mr-2">Attempts</span>
            {Array.from({ length: 5 }, (_, i) => (
              <span key={i} data-testid={`attempt-led-${i}`} className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: i < attempts ? "#fbbf24" : "#3f3f46", boxShadow: i < attempts ? "0 0 6px #fbbf2466" : "none" }} />
            ))}
          </div>
          {feedbacks.length > 0 && (
            <div data-testid="keypad-feedback" className="w-64 max-h-28 overflow-y-auto border border-white/10 bg-black/60" style={{ scrollbarWidth: "thin" }}>
              {feedbacks.map((f, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-1.5 border-b border-white/5">
                  <span className="font-mono text-sm tracking-[0.3em] text-zinc-300">{f.pin}</span>
                  <span className="ml-auto flex gap-1">
                    {Array.from({ length: f.exact }, (_, j) => <span key={`e${j}`} className="w-2 h-2 rounded-full bg-emerald-400" />)}
                    {Array.from({ length: f.misplaced }, (_, j) => <span key={`m${j}`} className="w-2 h-2 rounded-full bg-amber-400" />)}
                    {f.exact + f.misplaced === 0 && <span className="font-mono text-[8px] text-zinc-600 uppercase">no hits</span>}
                  </span>
                </div>
              ))}
              <p className="px-3 py-1.5 font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]"><span className="text-emerald-400">green</span> right digit, right place · <span className="text-amber-400">amber</span> right digit, wrong place</p>
            </div>
          )}
          <button data-testid="heist-abandon" onClick={abandon} className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-200 transition-colors">Walk away</button>
        </div>
      )}

      {/* RESULT */}
      {result && (
        <div data-testid="heist-result" className="flex flex-col items-center gap-4 px-6 text-center" style={{ animation: "ncFadeUp 0.4s ease both" }}>
          {result.status === "cracked" ? (
            <>
              <DoorOpen className="w-12 h-12 text-emerald-400" style={{ filter: "drop-shadow(0 0 20px #34d39988)" }} />
              <h2 className="font-black uppercase tracking-[0.15em] text-3xl text-emerald-400">{result.crew ? "They cracked it" : "It's open"}</h2>
              <p data-testid="heist-payout" className="font-mono text-xl font-bold text-zinc-100">{result.crew ? `+$${result.payout.toLocaleString()} crew cut` : `+$${result.payout.toLocaleString()}`}</p>
              <p className="font-mono text-[10px] text-amber-400 uppercase tracking-[0.2em] max-w-sm">Silent alarm {result.alarm_ref} hit the CAD anyway — units are rolling. Get out.</p>
            </>
          ) : (
            <>
              <ShieldAlert className="w-12 h-12 text-red-400" style={{ filter: "drop-shadow(0 0 20px #ef444488)" }} />
              <h2 className="font-black uppercase tracking-[0.15em] text-3xl text-red-400">{result.crew ? "Crowd panicked" : "Lockout"}</h2>
              <p className="font-mono text-[10px] text-red-300 uppercase tracking-[0.2em] max-w-sm">{result.crew ? "The room lost its nerve and the job blew — silent alarm is on the CAD." : `The keypad fried itself and silent alarm ${result.alarm_ref} is screaming on the dispatch board.`}</p>
            </>
          )}
          <button data-testid="heist-done" onClick={() => { setSession(null); setResult(null); setFound(0); setEntry(""); setAttempts(5); setFeedbacks([]); }}
            className="mt-3 px-8 py-3 font-mono text-[10px] uppercase tracking-[0.3em] border border-white/20 hover:border-white/50 transition-colors flex items-center gap-2">
            <CircleAlert className="w-4 h-4" /> Back to the shadows
          </button>
        </div>
      )}

      <style>{`@keyframes ncFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } } @keyframes ncPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
